import React, { FC, Fragment, ReactNode } from 'react';
import { QueryResult } from 'react-apollo';
import { FetchMoreOptions } from 'apollo-client';
import { clone } from 'lodash';

import { UserGroup } from 'types/app';
import { Content as ContentType, Feed, GetFeedComponent, GetFeedDocument } from 'api';
import { Dimensions, Loaders, QueryManager } from 'components';
import { DimensionsProvider, QueryParamsProvider } from 'providers';
import { NULL } from 'constants/index';

import Loader from '../components/Loader';
import PageHandler from '../components/PageHandler';

interface IProps {
  emptyState: () => JSX.Element;
  header?: ReactNode;
  selectedGroup?: UserGroup;
  variables: {
    sort: boolean;
    key: string;
    id: string;
  };
}

interface IChunkedResult {
  results: ContentType[];
  startIndex: number;
  endIndex: number;
}

const FEED_LIMIT = 10;
const LOADER_HEIGHT = 30;

const FeedManager: FC<IProps> = props => {
  const { emptyState, header, selectedGroup, variables } = props;

  const queryParams = {
    query: GetFeedDocument,
    variables: {
      limit: FEED_LIMIT,
      ...variables,
    },
  };

  function buildChunks(results: ContentType[]) {
    const clonedResults = clone(results);
    const chunkedResults: IChunkedResult[] = [];
    let startIndex = 0;

    while (clonedResults.length) {
      const chunk = clonedResults.splice(0, FEED_LIMIT);
      const endIndex = startIndex + chunk.length;

      chunkedResults.push({
        results: chunk,
        startIndex,
        endIndex,
      });

      startIndex = endIndex + 1;
    }

    return chunkedResults;
  }

  const updateQuery: FetchMoreOptions['updateQuery'] = (prev, options) => {
    if (!options.fetchMoreResult) {
      return prev;
    }

    const results = [...prev.getFeed.results, ...options.fetchMoreResult.getFeed.results];

    return {
      ...prev,
      getFeed: { ...options.fetchMoreResult.getFeed, results },
    };
  };

  function onLoadMoreRows(queryResult: QueryResult) {
    const { fetchMore, data, loading, error } = queryResult;
    const { getFeed } = data;
    const { id_lt, offset } = getFeed;

    if (loading || error || (!id_lt && !offset)) {
      return Promise.resolve();
    }

    return fetchMore({
      updateQuery,
      variables: { id_lt, offset },
    });
  }

  function renderLoadMoreLoader(loading: boolean) {
    if (loading) {
      return <Loaders.Standard display="block" height={LOADER_HEIGHT} />;
    }

    return <div style={{ height: LOADER_HEIGHT }} />;
  }

  return (
    <QueryParamsProvider value={queryParams}>
      {header}
      <GetFeedComponent notifyOnNetworkStatusChange fetchPolicy="network-only" {...queryParams}>
        {queryResult => (
          <QueryManager loader={<Loader />} {...queryResult}>
            {({ data, loading }) => {
              if (!data || !data.getFeed) {
                return null;
              }

              const { getFeed }: { getFeed: Feed } = data;
              const chunkedResults = buildChunks(getFeed.results);

              if (!chunkedResults.length) {
                return emptyState();
              }

              return (
                <Fragment>
                  <PageHandler
                    chunkedResults={chunkedResults}
                    selectedGroup={selectedGroup}
                    onLoadMoreRows={() => onLoadMoreRows(queryResult)}
                  />
                  {renderLoadMoreLoader(loading)}
                </Fragment>
              );
            }}
          </QueryManager>
        )}
      </GetFeedComponent>
    </QueryParamsProvider>
  );
};

FeedManager.defaultProps = {
  header: NULL,
  selectedGroup: NULL,
};

const FeedWrapper: FC<IProps> = props => (
  <Dimensions>
    {dimensions => (
      <DimensionsProvider value={{ dimensions }}>
        <FeedManager {...props} />
      </DimensionsProvider>
    )}
  </Dimensions>
);

export default FeedWrapper;
